/**
 * Single source of truth for all user-facing static text.
 *
 * Components import this and expose it to their template, e.g.:
 *
 *   protected readonly T = TEXT;
 *
 * Then templates use `{{ T.login.signIn }}` etc.
 *
 * Dynamic content (user names, API data) is NOT defined here.
 */
export const APP_SYSTEM_NAME = 'OmniFlow' as const;

export const TEXT = {
  // ─────────────────────────────────────────────────────────────────────
  // Common / shared
  // ─────────────────────────────────────────────────────────────────────
  common: {
    appName: APP_SYSTEM_NAME,
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    continue: 'Continue',
    save: 'Save',
    submit: 'Submit',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    search: 'Search',
    clear: 'Clear',
    export: 'Export',
    exporting: 'Exporting...',
    exportModal: {
      title: 'Export Status',
      savedInDownloads: 'Saved in Downloads.',
      stayHere: 'Stay Here',
      goToDownloads: 'Go to Downloads',
    },
    loading: 'Loading...',
    enabled: 'Enabled',
    disabled: 'Disabled',
    open: 'Open',
    nextStepEyebrow: 'NEXT STEP',
    selectLocation: 'Select Location',
    downloads: 'Downloads',
  },

  // ─────────────────────────────────────────────────────────────────────
  // Login + sign-in flow
  // ─────────────────────────────────────────────────────────────────────
  login: {
    brandPartner: 'Assurant',
    headlineLine1: 'Operational control,',
    headlineLine2: 'simplified.',
    subtitle:
      'A unified platform for receiving, item processing, and distribution center operations.',
    bullets: [
      'Standardized receiving workflows',
      'Real-time item and package visibility',
    ] as const,
    signInHeading: 'Sign in',
    signInButton: 'Sign In',
  },

  // ─────────────────────────────────────────────────────────────────────
  // Auth-check splash (login-landing)
  // ─────────────────────────────────────────────────────────────────────
  loginLanding: {
    signingYouIn: 'Signing you in…',
  },

  // ─────────────────────────────────────────────────────────────────────
  // Sidebar navigation
  // ─────────────────────────────────────────────────────────────────────
  sidebar: {
    closeMenuAria: 'Close menu',
    closeBackdropAria: 'Close navigation menu',
    signOut: 'Sign Out',
    signOutTitle: 'Sign out of OmniFlow',
    locationLockedTitle: 'Select a location from the Dashboard first',
    sections: {
      management: 'Management',
      dockReceiving: 'Dock Receiving',
      itemReceiving: 'Device Processing',
      rma: 'RMA',
      userManagement: 'User Management',
      configuration: 'Configuration',
    },
    items: {
      dashboard: 'Dashboard',
      receivePackages: 'Receive Packages',
      asnDashboard: 'ASN Dashboard',
      search: 'Search',
      receiveItems: 'Device Photo',
      devicePhotoSearch: 'Device Photo Search',
      secondaryClearDashboard: 'Secondary Clear Dashboard',
      secondaryClearProcessing: 'Secondary Clear Processing',
      rmaDashboard: 'RMA Dashboard',
      salesRmaProcessing: 'Sales RMA Processing',
      searchRma: 'Search RMA',
      userList: 'User List',
      dccConfiguration: 'DCC Configuration',
      deviceControlCenter: 'Device Control Center',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Device Control Center
  // ─────────────────────────────────────────────────────────────────────
  deviceControlCenter: {
    hub: {
      title: 'Device Control Center',
      subtitle: 'Choose a processing workflow below.',
      workflowsTitle: 'Processing Workflows',
      workflowsSubtitle:
        'Select a workflow to open the corresponding processing screen.',
      tileNoAccess: 'You do not have access to this workflow.',
      allNoAccess:
        'You do not have access to any processing workflows. You can still search for device details above.',
      tiles: {
        vip: {
          title: 'VIP Processing',
          description: 'Inspect devices by UID.',
        },
        screenProtector: {
          title: 'Screen Protector Removal',
          description: 'Remove screen protectors by UID.',
        },
        userLock: {
          title: 'User Lock',
          description: 'Manage device user lock by UID.',
        },
      },
    },
    backToHub: 'Back to Device Control Center',
    search: {
      searchTitle: 'IMEI Search',
      searchPlaceholder: 'Search by IMEI',
      submit: 'Search',
      searching: 'Searching…',
      clear: 'Clear',
      // errorRequired: 'Enter an IMEI, serial number, or UID to continue.',
      errorNotFound: 'No device found for that IMEI, serial number, or UID.',
      errorSearchFailed: 'Could not look up the device. Please try again.',
    },
    processingFlow: {
      title: 'Device Processing Flow',
      subtitle: 'Real-time position and processing status.',
      nextStation: 'Next Station',
    },
    regrade: {
      button: 'Regrade',
      modalTitle: 'Confirm Regrade',
      modalBodyPrefix: 'Are you sure you want to send device IMEI',
      modalBodySuffix: 'for regrading?',
      confirm: 'Yes, Regrade',
      success: 'Regrade Success',
      failure: 'Could not submit the regrade request. Please try again.',
    },
    details: {
      title: 'Device Details',
      fields: {
        imei: 'IMEI',
        serialNumber: 'Serial Number',
        type: 'Type',
        color: 'Color',
        capacity: 'Capacity',
        program: 'Program',
        assurantSku: 'Assurant SKU',
        customerSku: 'Client SKU',
      },
    },
    status: {
      title: 'Device Status',
      fields: {
        screenLock: 'Screen Lock',
        debugMode: 'Debug Mode',
        screenProtector: 'Screen Protector',
        dataErasure: 'Data Erasure',
        compliance: 'Compliance',
        exception: 'Exception',
      },
    },
    vip: {
      title: 'VIP Processing',
      subtitle:
        'Enter a UID to look up a device and complete the VIP inspection checklist.',
      searchTitle: 'UID Search',
      searchSubtitle:
        'Enter the device UID to look up its details and start the VIP checklist.',
      searchPlaceholder: 'Scan or enter device UID',
      info: 'Each device is looked up individually by its UID to validate details before processing.',
      nextStepDescription:
        'Review the device details and continue to the VIP inspection checklist.',
      submit: 'Submit',
      searching: 'Searching…',
      clear: 'Clear',
      errorRequired: 'Enter a device UID to continue.',
      errorNotFound: 'No device found for that UID.',
      errorSearchFailed: 'Could not look up the device. Please try again.',
      errorSubmitFailed:
        'Could not submit the VIP checklist. Please try again.',
      uidLabel: 'UID',
      uidCardTitle: 'Device UID',
      uidCardSubtitle: 'The device currently being processed.',
      deviceInfoTitle: 'Device Information',
      fields: {
        rma: 'RMA',
        trackingNumber: 'Tracking Number',
        dockReceiving: 'Dock Receiving',
      },
      questionnaire: {
        title: 'VIP Inspection Checklist',
        questionPrefix: 'Question',
        questionOf: 'of',
        questions: {
          boxSealed: 'Is the box sealed?',
          boxDamaged: 'Is the box damaged?',
        },
        imageAlt: {
          boxNotSealed:
            'Box not sealed workflow — remove the device, apply the UID label, add the screen-protector sticker, set the box aside, and place the phone on the conveyor.',
          pristine:
            'Pristine package workflow — remove the phone, perform erasure, flash the device, return to original packaging, re-tape, and place in the finished goods tote.',
          damaged:
            'Damaged package workflow — remove the phone, perform erasure, flash the device, return to original packaging, re-tape, and place in the finished goods tote. Use the existing damaged package exception process before saving.',
        },
        followStepsTitle: 'Follow These Steps',
        followStepsSubtitle: 'Complete all steps before proceeding.',
        confirmCheckboxLabel: 'I confirm all steps above are complete.',
        yes: 'Yes',
        no: 'No',
        previous: 'Previous',
        next: 'Next',
        cancel: 'Cancel',
        confirmContinue: 'Confirm & Continue',
        submit: 'Submit',
        submitting: 'Submitting…',
        done: 'Done',
        successTitle: 'Successfully Submitted',
        successSubtitle:
          'The VIP Processing record has been submitted successfully.',
      },
    },
    screenProtector: {
      title: 'Screen Protector Removal',
      subtitle:
        'Enter a UID to look up a device and log screen protector removal.',
      searchTitle: 'UID Search',
      searchSubtitle:
        'Enter the device UID to look up its details before removing the screen protector.',
      searchPlaceholder: 'Scan or enter device UID',
      info: 'Each device is looked up individually by its UID to validate details before processing.',
      nextStepDescription:
        'Review the device details and continue with screen protector removal.',
      submit: 'Submit',
      searching: 'Searching…',
      clear: 'Clear',
      errorRequired: 'Enter a device UID to continue.',
      errorNotFound: 'No device found for that UID.',
      errorSearchFailed: 'Could not look up the device. Please try again.',
      errorSubmitFailed:
        'Could not submit the screen protector removal. Please try again.',
      uidLabel: 'UID',
      uidCardTitle: 'Device UID',
      uidCardSubtitle: 'The device currently being processed.',
      deviceInfoTitle: 'Device Information',
      fields: {
        rma: 'RMA',
        trackingNumber: 'Tracking Number',
        videoUnboxing: 'Video Unboxing',
        screenProtectorPresent: 'Screen Protector Present',
      },
      presentYes: 'Yes',
      presentNo: 'No',
      question: {
        eyebrow: 'Question 1 of 1',
        text: 'Has the Screen Protector been removed successfully?',
        yes: 'Yes',
        no: 'No',
        submit: 'Submit',
        submitting: 'Submitting…',
        successTitle: 'Successfully Submitted',
        successSubtitle:
          'The screen protector removal has been recorded successfully.',
        done: 'Done',
      },
    },
    userLock: {
      title: 'User Lock',
      subtitle: 'Enter a UID to look up a device and check its user lock status.',
      searchTitle: 'UID Search',
      searchSubtitle:
        'Enter the device UID to look up its details before managing the user lock.',
      searchPlaceholder: 'Scan or enter device UID',
      info: 'Each device is looked up individually by its UID to validate details before processing.',
      nextStepDescription:
        'Review the device details and continue with the user lock decision.',
      submit: 'Submit',
      clear: 'Clear',
      searching: 'Searching…',
      errorRequired: 'Enter a device UID to continue.',
      errorNotFound: 'No device found for that UID.',
      errorSearchFailed: 'Could not look up the device. Please try again.',
      errorRecordFailed:
        'Could not record the user lock decision. Please try again.',
      uidLabel: 'UID',
      deviceInfoTitle: 'Device Information',
      fields: {
        rma: 'RMA',
        trackingNumber: 'Tracking Number',
        userLockStatus: 'User Lock Status',
      },
      statusLocked: 'Locked',
      statusUnlocked: 'Unlocked',
      question: {
        heading: 'User Lock Removed for UID?',
        yes: 'Yes',
        no: 'No',
        submit: 'Submit',
        submitting: 'Submitting…',
      },
      releaseModal: {
        title: 'Release Device',
        message: 'Place device on pallet and trigger release button.',
        ok: 'OK',
      },
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Dashboard / location picker
  // ─────────────────────────────────────────────────────────────────────
  dashboard: {
    title: 'Management Dashboard',
    subtitle: 'Select the warehouse location to begin.',
    welcomeBack: 'Welcome back',
    activeDcc: 'Active DCC',
    defaultBadge: 'Default',
    selectedLocationEyebrow: 'Selected Location',
    selectedPlaceholder: '—',
    selectedDescription:
      'Continue to start the dock receiving workflow for the selected warehouse.',
    noSelectionDescription: 'Choose a warehouse on the left to continue.',
    continue: 'Continue',
    emptyTitle: 'No locations assigned',
    emptyBody:
      'Contact your administrator to get access to a distribution center.',
    noAccessibleFeatures:
      'You do not have access to any features for this location.',
  },

  // ─────────────────────────────────────────────────────────────────────
  // DCC Configuration
  // ─────────────────────────────────────────────────────────────────────
  dccConfig: {
    title: 'DCC Configuration',
    subtitle:
      'Manage feature availability and warehouse assignment across distribution centers',
    addFeature: 'Add Feature',
    emptyTitle: 'No features configured yet',
    emptyBody: 'Click "Add Feature" above to create your first feature flag',
    selectDcc: 'Select DCC',
    selectDccLocation: 'Select a DCC location',
    assign: 'Assign',
    noChips: 'No DCCs assigned — use the selector to add one',
    dialog: {
      title: 'New Feature Flag',
      subtitle: 'Define a feature flag and assign DCC locations.',
      closeAria: 'Close dialog',
      sectionDetails: 'Feature Details',
      sectionGroups: 'DCC Groups',
      featureName: 'Feature Name',
      description: 'Description',
      enabledToggle: 'Enable this feature on save',
      enabledToggleDesc: 'Feature will be active immediately after saving.',
      dccGroups: 'DCC Groups',
      dccGroupsHelper:
        'Choose one or more distribution centers for this feature.',
      addGroup: 'Add Group',
      removeGroupAria: 'Remove Group',
      saveButton: 'Save Feature',
      featureNamePlaceholder: 'e.g. Dock Entry, Returns Flow, Item Scan…',
      descriptionPlaceholder:
        'Short description of what this feature controls…',
      requiredMark: '*',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // User List (User Management)
  // ─────────────────────────────────────────────────────────────────────
  userList: {
    title: 'User List',
    subtitle: 'Manage platform users, roles, and distribution center access.',
    addUser: 'Add User',
    searchPlaceholder: 'Search users',
    allRoles: 'All Roles',
    allLocations: 'All Locations',
    columns: {
      user: 'USER',
      email: 'EMAIL',
      dccs: 'DCCS',
      permissions: 'PERMISSIONS',
      actions: 'ACTIONS',
    },
    emptyDccs: '—',
    pager: {
      previous: 'Previous',
      next: 'Next',
      pageOf: 'Page',
    },
    emptyStateTitle: 'No users found',
    emptyStateBody:
      'Try adjusting your filters or click "Add User" to invite someone.',
    deleteDialog: {
      title: 'Delete User',
      messagePrefix: 'Are you sure you want to delete',
      messageSuffix: '? This action cannot be undone.',
      confirm: 'Delete',
    },
    tile: {
      noDccs: 'No DCCs assigned',
    },
    viewToggle: {
      table: 'Table view',
      tile: 'Tile view',
    },
    detailsDialog: {
      addTitle: 'Add User',
      editTitle: 'Edit User',
      addSubtitle: 'Add a new user with permissions and DCC access.',
      editSubtitle: 'Update user information, permissions, and access.',
      closeAria: 'Close dialog',
      contactInfo: 'Contact Info',
      firstName: 'First Name',
      firstNamePlaceholder: 'e.g. John',
      firstNameRequired: 'First Name is required',
      lastName: 'Last Name',
      lastNamePlaceholder: 'e.g. Smith',
      lastNameRequired: 'Last Name is required',
      email: 'Email',
      emailPlaceholder: 'e.g. john@company.com',
      emailRequired: 'Email is required',
      administrator: 'Administrator',
      administratorDescription:
        'Grant full admin privileges and system access.',
      dccPermissions: 'DCC Permissions',
      dccPermissionsHelper: 'Select the DCCs this user has access to.',
      functionalPermissions: 'Functional Permissions',
      functionalPermissionsHelper: 'Select the features this user can access.',
      clientProgramAccess: 'Customer / Program Access',
      clientProgramAccessHelper: 'Select Customer-Program access for this user',
      clientProgramSearchPlaceholder: 'Search by client or contract code...',
      clientProgramSelectedSuffix: 'selected',
      clientProgramNoResults: 'No matching client or contract.',
      saveButton: 'Save Changes',
      saveButtonAdd: 'Add User',
      savingButton: 'Saving...',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Questionnaire (Dock Receiving step 2)
  // ─────────────────────────────────────────────────────────────────────
  questionnaire: {
    pageTitle: 'Dock Receiving',
    intakeEyebrow: 'PACKAGE INTAKE',
    intakeTitle: 'Package Material Questionnaire',
    barcodeLabel: 'BARCODE',
    identifierLabel: 'RMA NUMBER / TRACKING NUMBER',
    questionPrefix: 'Question',
    questionOf: 'of',
    requiredError:
      'A response is needed for this question. Please provide an answer to continue.',
    answerAllRequired:
      'Please answer every question before submitting. No question may be left blank.',
    textAnswerPlaceholder: 'Type your answer here…',
    needHelpDefault: 'Need help?',
    viewGuideDefault: 'View Guide',
    guideModal: {
      closeAria: 'Close guide',
      importantLabel: 'IMPORTANT',
      statusCorrect: 'CORRECT',
      statusIncorrect: 'NOT CORRECT',
      statusConditional: 'CORRECT IF BOX HAS BOTH',
    },
    previousButton: 'Previous',
    nextButton: 'Next',
    errorState:
      'No questionnaire found for this package or failed to load questionnaire.',
    loadFailedFallback: 'An error occurred while loading the questionnaire.',
    confirmSuccess: 'Dock receipt confirmed successfully.',
    confirmFailure: 'Failed to confirm dock receipt.',
  },

  // ─────────────────────────────────────────────────────────────────────
  // Dock Receiving — Scan step
  // ─────────────────────────────────────────────────────────────────────
  dockReceiving: {
    pageTitle: 'Dock Receiving',
    cardTitle: 'Receive a Package',
    cardSubtitle:
      'You only need one identifier — enter a tracking number or an RMA number to continue.',
    sectionTitle: 'Enter One Identifier',
    sectionSubtitle: 'You only need to enter one field to continue',
    trackingLabel: 'TRACKING NUMBER',
    trackingPlaceholder: 'Enter tracking number',
    trackingHelper: 'Enter the full tracking number.',
    orDivider: 'OR',
    rmaLabel: 'RMA NUMBER',
    rmaPlaceholder: 'Enter RMA number',
    rmaHelper: 'Enter an RMA Number if provided.',
    inputPlaceholder: 'Scan or enter barcode here',
    info: 'Barcode scanners can submit directly into the input field for faster receiving.',
    nextStepDescription:
      'Proceed to package validation and questionnaire after scan.',
    submitButton: 'Receive Package',
    clearButton: 'Clear',
    errorNotFound: 'Barcode not found. Check the label or scan again.',
    errorRmaNotFound: 'RMA not found. Check the number or scan again.',
    stepIndicator: {
      scan: 'Scan',
      questionnaire: 'Questionnaire',
      summary: 'Summary',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Item Receiving — IMEI step + others
  // ─────────────────────────────────────────────────────────────────────
  itemReceiving: {
    pageTitle: 'Device Receiving',
    imei: {
      cardTitle: 'Scan or enter IMEI / Serial Number',
      cardSubtitle:
        'Scan each item barcode individually to validate product details and continue item intake.',
      inputPlaceholder: 'Scan or enter item barcode here',
      info: 'Each item should be scanned separately to ensure accurate receiving and inventory tracking.',
      nextStepDescription:
        'Review item details and continue to confirmation after each successful scan.',
      submitButton: 'Take Photos',
      errorRequired: 'Enter or scan an IMEI / serial number to continue.',
    },
    capture: {
      eyebrow: 'DEVICE RECEIVING',
      heading: 'Device Condition & Photos',
      subtitle:
        'Capture the device condition and photo evidence before moving to annotation.',
      imeiLabel: 'IMEI',
      imeiPrefix: 'IMEI :',
      datePrefix: 'Date :',
      conditionLabel: 'Condition assessment',
      conditionSubtitle: 'Select one condition grade to continue.',
      conditionRequired: 'Required',
      conditionSelected: 'Selected:',
      photosLabel: 'Photo evidence',
      photosSubtitle: 'Multiple angles are recommended for audit quality.',
      capturedSuffix: 'captured',
      capturedPreviews: 'Captured previews',
      takePhoto: 'Take Photo',
      useCamera: 'Use camera',
      upload: 'Upload',
      fromFiles: 'From files',
      multipleAnglesHint: 'Multiple angles recommended',
      annotateButton: 'Annotate Photos',
      backButton: 'Back',
      conditions: {
        excellent: { label: 'Excellent', desc: 'No visible wear' },
        good: { label: 'Good', desc: 'Minor scratches' },
        fair: { label: 'Fair', desc: 'Visible scratches' },
        damaged: { label: 'Damaged', desc: 'Significant damage' },
      },
      camera: {
        heading: 'Take Photos',
        capturedSuffix: 'captured',
        closeAria: 'Close camera',
        done: 'Done',
        capture: 'Capture',
      },
    },
    annotation: {
      heading: 'Annotate',
      devicePhotoLabel: 'Device photo',
      hintWithTool:
        'Click on the image to place a marker — or drag existing markers to reposition',
      hintWithoutTool: 'Select a tool above, then click on the image',
      clearAll: 'Clear All',
      capturedPhotos: 'CAPTURED PHOTOS',
      marksSuffix: 'marks',
      reviewButton: 'Review',
      backButton: 'Back',
      tools: {
        scratch: 'Scratch',
        dent: 'Dent',
        crack: 'Crack',
      },
    },
    summary: {
      eyebrow: 'DEVICE RECEIVING',
      heading: 'Review & Submit',
      subtitle: 'Confirm details and submit. You can edit any section.',
      readyBadge: 'Ready',
      readyBadgeLong: 'Ready for submission',
      summaryTitle: 'Summary',
      annotatedPhotosTitle: 'Annotated Photos',
      annotatedPhotosSubtitle: 'Per-angle annotation summary',
      submissionTitle: 'Submission',
      submissionStatus: 'Status',
      actionsTitle: 'Actions',
      backToAnnotate: 'Back to Annotate',
      editLink: 'Edit',
      labelImei: 'IMEI',
      labelCondition: 'Condition',
      labelPhotos: 'Photos',
      labelAnnotations: 'Annotations',
      annotatedPhotos: 'Annotated Photos',
      notesLabel: 'Notes',
      notesOptional: 'Optional',
      notesPlaceholder: 'Add any additional notes about this device…',
      uploadingButton: 'Uploading…',
      submitButton: 'Submit',
      successHeading: 'Submitted!',
      successMessagePrefix: 'Device',
      successMessageSuffix: 'Photos have been submitted successfully.',
      receiveAnother: 'Scan Next IMEI',
      uploadFailedFallback: 'Upload failed. Please try again.',
      marksSuffix: 'marks',
    },
    stepIndicator: {
      imei: 'IMEI',
      capture: 'Capture',
      annotate: 'Annotate',
      review: 'Review',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Secondary Clear Processing
  // ─────────────────────────────────────────────────────────────────────
  secondaryClear: {
    pageTitle: 'Secondary Clear Processing',
    fields: {
      customerProgram: 'Customer / Program',
      customerProgramPlaceholder: 'Select Customer / Program',
      customerProgramLoading: 'Loading…',
      ceItemNumber: 'CE Item Number',
      serialNumber: 'Serial Number',
      levelOfProcessing: 'Level of Processing',
      failureReason: 'Failure Reason',
      failureReasonPlaceholder: 'Type code or description to search',
    },
    levels: {
      level1: 'Level 1',
      level2: 'Level 2',
      level3: 'Level 3',
      level4: 'Level 4',
    },
    actions: {
      clear: 'Clear',
      submit: 'Submit',
      submitting: 'Submitting…',
    },
    errors: {
      required: 'Required',
      ceItemFormat: 'CE Item Number must be 9 digits and start with 708',
      serialFormat: 'Serial Number must be exactly 15 digits',
      submitFailed: 'Submission failed. Please try again.',
      noFailureReasonMatches: 'No matching failure reasons.',
      noCustomerProgramMatches: 'No matching Customer / Program.',
      clientContractsLoadFailed:
        'Failed to load Customer / Program list. Please refresh and try again.',
      noClientContractsAssigned:
        'No Customer / Program is assigned to your account. Contact your administrator.',
    },
    toast: {
      success: 'Secondary clear recorded successfully.',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Search Dock (search results page)
  // ─────────────────────────────────────────────────────────────────────
  searchDock: {
    pageTitle: 'Search Packages',
    filtersHeading: 'FILTERS',
    fields: {
      tracking: 'TRACKING NUMBER',
      trackingPlaceholder: 'Enter tracking number',
      rma: 'RMA NUMBER',
      rmaPlaceholder: 'Enter RMA number',
      customer: 'CUSTOMER / PROGRAM',
      customerPlaceholder: 'All customers',
      status: 'STATUS',
      statusPlaceholder: 'All statuses',
      dateRange: 'DOCK SCAN DATE AND TIME',
      dateRangePlaceholder: 'Choose date & time range',
    },
    statusOptions: [
      { label: 'Dock Received', value: 'Dock Received' },
      { label: 'Dock Reject', value: 'Dock Reject' },
      { label: 'Dock Processing', value: 'Dock Processing' },
    ],
    actions: {
      search: 'Search',
      clear: 'Clear',
      export: 'Export',
      exporting: 'Exporting...',
      edit: 'Edit',
    },
    receivedPackages: 'Received Packages',
    shownSuffix: 'shown',
    columns: {
      tracking: 'Tracking #',
      status: 'Status',
      rma: 'RMA',
      clientProgram: 'Client / Program',
      scannedBy: 'Scanned By',
      date: 'Dock Scan Date and Time',
      actions: 'Actions',
    },
    editQuestionnaire: 'Edit Questionnaire',
    viewImages: 'View Images',
    modal: {
      title: 'Package Images',
      imagesFound: 'images found',
      imageFound: 'image found',
      loading: 'Loading images...',
      empty: 'No images found for this package.',
      close: 'Close',
      download: 'Download',
      downloadError: 'Could not download the image. Please try again.',
    },
    emptyTitle: 'No records found',
    emptyBody:
      'No packages match your search criteria. Try adjusting your filters.',
    rmaPrefix: 'RMA:',
    questionsAnsweredSuffix: 'questions answered',
    popover: {
      summaryPrefix: 'Summary — Row',
      noQuestions: 'No questions found',
    },
    viewToggle: {
      tile: 'Tile view',
      table: 'Table view',
    },
    errors: {
      dateRangeRequired: 'Date is required',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Secondary Clear Dashboard
  // ─────────────────────────────────────────────────────────────────────
  secondaryClearDashboard: {
    pageTitle: 'Secondary Clear Processing Dashboard',
    subtitle:
      'Manager view of processed devices, repair mix, and failure reason trends.',
    filtersHeading: 'FILTERS',
    fields: {
      dateRange: 'DATE',
      dateRangePlaceholder: 'Choose date range',
      ceItemNumber: 'CE ITEM NUMBER',
      ceItemNumberPlaceholder: 'Enter CE item number',
      levelOfProcessing: 'LEVEL OF PROCESSING',
      levelOfProcessingPlaceholder: 'Choose level(s)',
      failureReason: 'FAILURE REASON',
      failureReasonPlaceholder: 'Choose failure reason(s)',
      customerProgram: 'CUSTOMER / PROGRAM',
      customerProgramPlaceholder: 'Choose customer/program',
      user: 'USER',
      userPlaceholder: 'Enter user',
    },
    actions: {
      search: 'Search',
      clear: 'Clear',
      export: 'Export',
      exporting: 'Exporting...',
    },
    metrics: {
      devicesProcessed: 'Devices Processed',
    },
    charts: {
      devicesByLevel: 'Devices by Level of Processing',
      devicesByFailureReason: 'Devices by Failure Reason',
      placeholder: 'Charts coming soon',
    },
    processedDevices: 'Processed Devices',
    shownSuffix: 'shown',
    columns: {
      processedDate: 'Processed Date',
      ceItemNumber: 'CE Item Number',
      serialNumber: 'Serial Number',
      customerProgram: 'Customer / Program',
      level: 'Level of Processing',
      failureReason: 'Failure Reason',
      user: 'User',
    },
    emptyTitle: 'No records found',
    emptyBody:
      'No processed devices match your search criteria. Try adjusting your filters.',
    selectDateTitle: 'Select a date range',
    selectDateBody:
      'Choose a date range and click Search to view processed devices.',
    errors: {
      dateRangeRequired: 'Date is required',
    },
    viewToggle: {
      tile: 'Tile view',
      table: 'Table view',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Device Photo Search
  // ─────────────────────────────────────────────────────────────────────
  devicePhotoSearch: {
    pageTitle: 'Device Photo Search',
    filtersHeading: 'FILTERS',
    fields: {
      imeiSerial: 'IMEI / SERIAL NUMBER',
      imeiSerialPlaceholder: 'Enter IMEI or serial number',
      uploadedBy: 'UPLOADED BY',
      uploadedByPlaceholder: 'Enter uploaded by',
      uploadedDate: 'UPLOADED DATE & TIME',
      uploadedDatePlaceholder: 'Choose date & time range',
    },
    actions: {
      search: 'Search',
      clear: 'Clear',
      export: 'Export',
      exporting: 'Exporting...',
      viewImages: 'View Images',
    },
    results: 'Device Photo Results',
    viewToggle: {
      tile: 'Tile view',
      table: 'Table view',
    },
    columns: {
      imeiSerial: 'IMEI / Serial Number',
      uploadedBy: 'Uploaded By',
      uploadedOn: 'Uploaded Date & Time',
      action: 'Action',
    },
    emptyTitle: 'No records found',
    emptyBody:
      'No device photos match your search criteria. Try adjusting your filters.',
    selectDateTitle: 'Select a date & time range',
    selectDateBody:
      'Choose a date & time range and click Search to view device photos.',
    errors: {
      dateRangeRequired: 'Date is required',
      exportUnavailable: 'Export is not available yet',
    },
    modal: {
      title: 'Device Photos',
      imagesFound: 'images found',
      imageFound: 'image found',
      loading: 'Loading images...',
      empty: 'No images found for this record.',
      front: 'Front',
      back: 'Back',
      record: 'Record',
      close: 'Close',
      download: 'Download',
      downloadError: 'Could not download the image. Please try again.',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // User Profile
  // ─────────────────────────────────────────────────────────────────────
  userProfile: {
    pageTitle: 'User Profile',
    fields: {
      name: 'Name',
      email: 'Email',
      theme: 'Theme',
    },
    preferences: {
      eyebrow: 'PREFERENCES',
      title: 'Default settings',
      subtitle: 'Set your default DCC',
      dccLabel: 'DCC',
      dccPlaceholder: 'Select a DCC',
      save: 'Save preferences',
      saving: 'Saving…',
      saved: 'Default DCC saved.',
      saveFailed: 'Could not save your default DCC. Please try again.',
      noDccs: 'No DCCs are assigned to your account.',
    },
    back: 'Back',
    signOut: 'Sign Out',
  },

  // ─────────────────────────────────────────────────────────────────────
  // Returns Receiving
  // ─────────────────────────────────────────────────────────────────────
  returnsReceiving: {
    pageTitle: 'Returns Receiving',
    subtitle: 'Process return shipments here',
    emptyMessage: 'No returns to process at the moment',
  },

  // ─────────────────────────────────────────────────────────────────────
  // RMA Search
  // ─────────────────────────────────────────────────────────────────────
  rmaSearch: {
    pageTitle: 'Search RMA',
    filtersTitle: 'Search',
    filtersSubtitle:
      'Search by IMEI, RMA number, channel, compliance, or received date.',
    filtersHeading: 'Filters',
    processedByDate: 'Processed by Date',
    lineLevelResults: 'Line-Level Results',
    shownSuffix: 'shown',
    fields: {
      imei: 'IMEI',
      imeiPlaceholder: 'Enter IMEI',
      rmaNumber: 'RMA NUMBER',
      rmaPlaceholder: 'Enter RMA number',
      channel: 'CHANNEL',
      channelPlaceholder: 'All Channels',
      compliance: 'COMPLIANCE',
      compliancePlaceholder: 'All Statuses',
      receivedDate: 'RECEIVED DATE & TIME',
      fromPlaceholder: 'From MM-DD-YYYY hh:mm',
      toPlaceholder: 'To MM-DD-YYYY hh:mm',
      dateSeparator: 'TO',
    },
    complianceOptions: [
      { label: 'Compliant', value: 'Compliant' },
      { label: 'Non-Compliant', value: 'Non-Compliant' },
    ] as const,
    actions: {
      search: 'Search',
      clear: 'Clear',
      export: 'Export',
      exporting: 'Exporting…',
    },
    stats: {
      totalLines: 'Total Lines',
      totalLinesSub: 'All matching results',
      compliant: 'Compliant',
      compliantSub: 'Cleared lines',
      nonCompliant: 'Non-Compliant',
      nonCompliantSub: 'Needs action',
      compliantRate: 'Compliant Rate',
      compliantRateSub: 'Reviewed lines only',
    },
    dateGroupColumns: {
      date: 'DATE',
      totalLines: 'TOTAL LINES',
      compliant: 'COMPLIANT',
      nonCompliant: 'NON-COMPLIANT',
    },
    lineColumns: {
      rmaNumber: 'RMA NUMBER',
      channel: 'CHANNEL',
      buyerName: 'BUYER NAME',
      receivedDate: 'RECEIVED DATE',
      imei: 'IMEI',
      sku: 'SKU',
      description: 'DESCRIPTION',
      reasonCode: 'BUYER REASON CODE',
      dccReasonCode: 'DCC REASON CODE',
      compliance: 'COMPLIANCE',
    },
    tileLabels: {
      buyerReason: 'Buyer Reason',
      dccReason: 'DCC Reason',
    },
    emptyTitle: 'No records found',
    emptyBody:
      'No RMA lines match your search criteria. Try adjusting your filters.',
    errors: {
      dateRangeRequired: 'Received date range is required.',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Access Denied
  // ─────────────────────────────────────────────────────────────────────
  accessDenied: {
    pageTitle: 'Access Denied',
  },

  // ─────────────────────────────────────────────────────────────────────
  // Downloads (export jobs)
  // ─────────────────────────────────────────────────────────────────────
  downloads: {
    pageTitle: 'Download Exported Files',
    subtitle: 'Files exported from Omniflow screens.',
    columns: {
      fileName: 'File Name',
      exportType: 'Export Type',
      exportedAt: 'Exported At',
      status: 'Status',
      action: 'Action',
    },
    actions: {
      download: 'Download',
      downloading: 'Downloading...',
      refresh: 'Refresh',
    },
    resultTitle: 'Export Jobs',
    shownSuffix: 'shown',
    fileProcessing: 'File is being processed…',
    viewToggle: {
      tile: 'Tile view',
      table: 'Table view',
    },
    empty: {
      title: 'No exports yet',
      body: 'Files you export from Omniflow screens will appear here.',
    },
    errors: {
      loadFailed: 'Could not load export jobs. Please try again.',
      downloadFailed: 'Could not download the file. Please try again.',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Sales RMA Processing
  // ─────────────────────────────────────────────────────────────────────
  salesRmaProcessing: {
    title: 'Sales RMA Processing',
    processImei: {
      sectionLabel: 'DEVICE RECEIVING',
      title: 'Process by IMEI',
      desc: 'Scan or enter an IMEI.',
      startBtn: 'Start',
      enterLabel: 'Enter IMEI',
      enterSubtitle: 'Scan or manually enter the device IMEI.',
      inputLabel: 'IMEI NUMBER',
      inputPlaceholder: 'Scan or enter barcode here',
      inputHelper: 'Enter the full IMEI number.',
      clearBtn: 'Clear',
      scanBtn: 'Scan',
    },
    processRma: {
      sectionLabel: 'RMA RECEIVING',
      title: 'Process by RMA',
      desc: 'Search an existing RMA.',
      searchBtn: 'Search',
      notAvailable: 'Process by RMA is not yet available.',
    },
    noRmaFound: {
      title: 'No Linked RMA Found',
      desc: 'Follow process defined by AVR.',
    },
    rmaDetails: {
      sectionTitle: 'RMA Details',
      viewDetailsBtn: 'View Details',
      fields: {
        rmaNumber: 'RMA NUMBER',
        channel: 'CHANNEL',
        buyer: 'BUYER',
        status: 'STATUS',
      },
    },
    processDevice: {
      sectionTitle: 'Process Device',
      scannedBadge: 'Scanned',
      buyerReasonCodeLabel: 'Buyer Reason Code',
      reasonCodeLabel: 'DCC Reason Code',
      reasonCodePlaceholder: 'Select DCC reason code',
      complianceLabel: 'Compliance',
      compliant: 'Compliant',
      nonCompliant: 'Non Compliant',
      commentsLabel: 'Comments',
      commentsPlaceholder: 'Add notes...',
      photoLabel: 'Photo',
      photoHint: 'Multiple angles are recommended for audit quality.',
      takePhoto: 'Take Photo',
      takePhotoSub: 'Use camera',
      uploadPhoto: 'Upload Photo',
      uploadPhotoSub: 'Browse files',
      capturedPreviews: 'Captured previews',
      capturedSuffix: 'captured',
    },
    detailsModal: {
      heading: 'RMA DETAILS',
      closeBtn: 'Close',
      fields: {
        orderNumber: 'ORDER NUMBER',
        channel: 'CHANNEL',
        buyerName: 'BUYER NAME',
        returnReason: 'RETURN REASON',
        createDate: 'CREATE DATE',
        status: 'STATUS',
        tracking: 'TRACKING',
        receivedBy: 'RECEIVED BY',
        receivedDate: 'RECEIVED DATE',
        totalLines: 'TOTAL LINES',
      },
      linesHeading: 'RMA Lines',
      columns: {
        imei: 'IMEI',
        sku: 'SKU',
        description: 'Description',
        reasonCode: 'Buyer Reason Code',
        compliance: 'Compliance',
      },
      currentBadge: 'Current',
      currentNote: 'Highlighted row is the currently scanned IMEI.',
    },
    camera: {
      heading: 'Camera',
      capturedSuffix: 'captured',
      closeAria: 'Close camera',
      done: 'Done',
      capture: 'Capture',
    },
    errors: {
      submitFailed: 'Submission failed. Please try again.',
      configLoadFailed:
        'Failed to load configuration. Please refresh and try again.',
      alreadyProcessed: 'This device has already been processed.',
    },
    toast: {
      submitSuccess: 'Device processed successfully.',
      submitError: 'Failed to submit. Please try again.',
    },
    backBtn: '← Back',
    submitBtn: 'Submit',
  },
  // ─────────────────────────────────────────────────────────────────────
  // RMA Dashboard
  // ─────────────────────────────────────────────────────────────────────
  rmadashboard: {
    title: 'RMA Dashboard',
    filtersHeading: 'FILTERS',
    fields: {
      receivedDate: 'RECEIVED DATE & TIME',
      fromPlaceholder: 'From MM-DD-YYYY hh:mm',
      toPlaceholder: 'To MM-DD-YYYY hh:mm',
      dateSeparator: 'TO',
      channel: 'CHANNEL',
      channelPlaceholder: 'All Channels',
      operator: 'OPERATOR',
      operatorPlaceholder: 'Enter operator',
      rmaNumber: 'RMA #',
      rmaNumberPlaceholder: 'Enter RMA number',
      trackingNumber: 'TRACKING #',
      trackingNumberPlaceholder: 'Enter tracking number',
    },
    actions: {
      search: 'Search',
      clear: 'Clear',
    },
    errors: {
      dateRangeRequired: 'Received date range is required.',
      dateRangeTooLarge: 'Date range cannot exceed 30 days.',
      loadFailed: 'No Records Found.',
    },
    compliance: {
      title: 'Compliance Summary',
      subtitle: 'Line outcomes by review status.',
      compliant: 'Compliant',
      nonCompliant: 'Non-Compliant',
      pending: 'Pending',
      rateTitle: 'COMPLIANT RATE',
      reviewedSuffix: 'reviewed lines',
    },
    charts: {
      byChannel: 'Received by Channel',
      byOperator: 'Received by Operator',
    },
    productivity: {
      title: 'Operator Labor Productivity',
      shownSuffix: 'shown',
      columns: {
        operator: 'OPERATOR',
        rmasHandled: 'RMAS HANDLED',
        linesSubmitted: 'LINES SUBMITTED',
        linesFailed: 'LINES FAILED',
        compliant: 'COMPLIANT',
        nonCompliant: 'NON-COMPLIANT',
        compliantRate: 'COMPLIANT RATE',
      },
    },
    emptyState: {
      title: 'No data available',
      body: 'No results match your search criteria.',
      errorTitle: 'No records found',
      errorBody:
        'No records found for your search criteria. Try adjusting your filters.',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // ASN Dashboard
  // ─────────────────────────────────────────────────────────────────────
  asndashboard: {
    title: 'ASN Dashboard',
    searchTitle: 'Search',
    searchSubtitle:
      'Search by RMA, tracking, customer program, or RMA creation date.',
    filtersLabel: 'FILTERS',
    fields: {
      rmaCreationDate: 'RMA CREATION DATE',
      rmaCreationDatePlaceholder: 'Choose date & time range',
      rmaNumber: 'RMA #',
      rmaNumberPlaceholder: 'Enter RMA number',
      trackingNumber: 'TRACKING #',
      trackingNumberPlaceholder: 'Enter tracking number',
      customer: 'CUSTOMER / PROGRAM',
      customerPlaceholder: 'All customers',
    },
    actions: {
      search: 'Search',
      clear: 'Clear',
    },
    errors: {
      dateRangeRequired: 'RMA creation date range is required.',
    },
    kpis: {
      totalAsns: 'Total ASNs',
      totalDockReceived: 'Dock Received',
      units: 'Units',
    },
    results: {
      title: 'Inbound Shipments',
      columns: {
        rmaNumber: 'RMA #',
        customerProgram: 'Customer Program',
        trackingNumber: 'Tracking #',
        units: 'Units',
        dockScanTimestamp: 'Dock Scan Timestamp',
        status: 'Status',
      },
      shownSuffix: 'shown',
      showing: 'Showing',
      ofLabel: 'of',
      recordsSuffix: 'shipments',
      pageOf: 'Page',
      previous: 'Previous',
      next: 'Next',
      emptyTitle: 'No shipments found',
      emptyBody: 'No inbound shipments match your search criteria.',
    },
    detailsModal: {
      heading: 'ASN Details',
      shipmentOverviewHeading: 'Shipment Overview',
      closeBtn: 'Close',
      fields: {
        carrier: 'Carrier',
        trackingNumber: 'Tracking #',
        dockScanTime: 'Dock Scan Time',
        status: 'Status',
      },
      linesHeading: 'Line Items',
      columns: {
        imei: 'IMEI',
        lineType: 'Type',
      },
    },
  },
} as const;

export type AppText = typeof TEXT;
